import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { store } from '@/lib/states/store'
import { SYSTEM_ORG_UUID } from '@/lib/supabase/supabaseClient'
import { queryClient } from '@/lib/utils/queryUtils'
import { duplicateMealPlan } from '@/lib/nutrition/duplicateMealPlan'
import { useCallback, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { VirtualizedDataTable } from '@/components/dataTable/VirtualizedDataTable'
import { ColumnDef } from '@tanstack/react-table'
import { Copy, MoreHorizontal, Plus, Trash2, Pencil, Link2 } from 'lucide-react'
import { errorToast } from '@/lib/utils/ToastUtils'
import { trpc } from '@/lib/trpc/client'
import { trpcVanilla } from '@/lib/trpc/vanilla'
import { invalidateCoachOnboardingQueries } from '@/lib/utils/onboardingCache'
import { toast } from 'sonner'
import LoadingSpinner from '@/components/LoadingSpinner'
import ClientSelectDialog from '@/components/templates/ClientSelectDialog'
import { LinkedClientAvatarStack } from '@/components/templates/LinkedClientAvatarStack'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Utensils } from 'lucide-react'
import DeleteAlert from '@/components/DeleteAlert'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useOrg } from '@/lib/states/session/hooks'
import {
  patchListCache,
  prependToList,
  removeFromList,
} from '@trainrr/api-client'

type TemplateLinkedClient = Awaited<
  ReturnType<typeof trpcVanilla.nutrition.listTemplates.query>
>[number]['linkedClients'][number]

type TemplateRow = {
  id: string
  name: string
  org_id: string | null
  meal_count: number
  linked_clients: TemplateLinkedClient[]
  is_system: boolean
}

export const Route = createFileRoute('/_auth/_dashboard/nutrition/')({
  component: NutritionTemplateLibrary,
})

function NutritionTemplateLibrary() {
  const org = useOrg()
  const utils = trpc.useUtils()
  const saveMealPlan = trpc.nutrition.saveMealPlan.useMutation()
  const completeOnboardingItem = trpc.onboarding.completeItem.useMutation()
  const navigate = useNavigate()
  const { data: templateRows, isPending: isTemplatesPending } =
    trpc.nutrition.listTemplates.useQuery(undefined, {
      enabled: Boolean(org?.id),
    })
  const { data: clients } = trpc.nutrition.listAssignableClients.useQuery(
    undefined,
    { enabled: Boolean(org?.id) },
  )

  const templates = useMemo<TemplateRow[]>(() => {
    return (templateRows ?? []).map((row) => ({
      id: row.id,
      name: row.name,
      org_id: row.org_id,
      meal_count: row.mealCount,
      linked_clients: row.linkedClients,
      is_system: row.org_id === SYSTEM_ORG_UUID,
    }))
  }, [templateRows])

  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const [creatingTemplate, setCreatingTemplate] = useState(false)
  const [newTemplateName, setNewTemplateName] = useState('')
  const [duplicateTemplate, setDuplicateTemplate] =
    useState<TemplateRow | null>(null)
  const [duplicateTemplateName, setDuplicateTemplateName] = useState('')
  const [duplicateLoading, setDuplicateLoading] = useState(false)
  const [deleteTemplate, setDeleteTemplate] = useState<TemplateRow | null>(null)
  const [manageDialogOpen, setManageDialogOpen] = useState(false)
  const [manageDialogTemplateId, setManageDialogTemplateId] = useState<
    string | null
  >(null)
  const manageDialogTemplate =
    templates.find((template) => template.id === manageDialogTemplateId) ?? null

  const deletePlan = trpc.nutrition.deletePlan.useMutation({
    onSuccess: (_data, variables) => {
      patchListCache(utils.nutrition.listTemplates, (old) =>
        removeFromList(old, variables.planId),
      )
    },
    onError: (error) => {
      console.error(error)
      errorToast(error)
    },
  })
  const patchLinkedClients = (
    planId: string,
    linkedClients: TemplateLinkedClient[],
  ) => {
    patchListCache(utils.nutrition.listTemplates, (old) =>
      old.map((row) => (row.id === planId ? { ...row, linkedClients } : row)),
    )
  }

  const setAssignments = trpc.nutrition.setAssignments.useMutation({
    onSuccess: (result) => {
      patchLinkedClients(result.planId, result.linkedClients)
    },
    onError: (error) => {
      console.error(error)
      errorToast(error)
    },
  })
  const assignToClient = trpc.nutrition.assignToClient.useMutation()
  const unlinkFromClient = trpc.nutrition.unlinkFromClient.useMutation()

  const handleCreateTemplate = async () => {
    if (creatingTemplate) return
    const orgId = store.getState().session.org?.id
    if (!orgId) return
    const trimmed = newTemplateName.trim()
    if (!trimmed) {
      toast.error('Please enter a template name.')
      return
    }

    setCreatingTemplate(true)
    const params = {
      org_id: orgId,
      meal_plan_id: null,
      name: trimmed,
      client_id: null,
      meals: [],
      deleted_meals: [],
      deleted_foods: [],
      is_template: true,
    }

    let data: string | undefined
    try {
      const saved = await saveMealPlan.mutateAsync({ params })
      data = saved.id
    } catch (error) {
      console.error(error)
      errorToast(error instanceof Error ? error : null)
      setCreatingTemplate(false)
      return
    }

    if (!data) {
      errorToast(null, 'Failed to create template')
      setCreatingTemplate(false)
      return
    }

    const createdPlanId = data
    patchListCache(utils.nutrition.listTemplates, (old) =>
      prependToList(old, {
        id: createdPlanId,
        name: trimmed,
        org_id: orgId,
        is_template: true,
        mealCount: 0,
        linkedClients: [],
      }),
    )

    setNewDialogOpen(false)
    setNewTemplateName('')
    setCreatingTemplate(false)
    try {
      await completeOnboardingItem.mutateAsync({
        key: 'coach_onboarding',
        version: 1,
        itemKey: 'setup.meals.create',
        source: 'auto',
        metadata: { source: 'meal-template-create', meal_plan_id: data },
      })
      await invalidateCoachOnboardingQueries(queryClient)
    } catch {
      // Onboarding completion is best-effort.
    }
    navigate({
      to: '/nutrition/$templateId',
      params: { templateId: data },
    })
  }

  const handleOpenDuplicateTemplate = useCallback((template: TemplateRow) => {
    setDuplicateTemplate(template)
    setDuplicateTemplateName(`${template.name} (copy)`)
  }, [])

  const handleConfirmDuplicateTemplate = async () => {
    const orgId = store.getState().session.org?.id
    const trimmedName = duplicateTemplateName.trim()

    if (!orgId || !duplicateTemplate) {
      return
    }

    if (!trimmedName) {
      toast.error('Please enter a template name.')
      return
    }

    setDuplicateLoading(true)

    try {
      const sourcePlan = await trpcVanilla.nutrition.getPlan.query({
        planId: duplicateTemplate.id,
      })

      const { imageCopyFailed, mealPlanId } = await duplicateMealPlan({
        orgId,
        name: trimmedName,
        meals: [...(sourcePlan.meals ?? [])].sort(
          (a, b) => (a.meal_number ?? 0) - (b.meal_number ?? 0),
        ),
        isTemplate: true,
        saveMealPlan: async (params) =>
          (await saveMealPlan.mutateAsync({ params })).id,
      })

      patchListCache(utils.nutrition.listTemplates, (old) =>
        prependToList(old, {
          id: mealPlanId,
          name: trimmedName,
          org_id: orgId,
          is_template: true,
          mealCount: sourcePlan.meals?.length ?? 0,
          linkedClients: [],
        }),
      )
      setDuplicateTemplate(null)
      toast.success(
        imageCopyFailed
          ? 'Template duplicated. Some meal images could not be copied.'
          : 'Template duplicated',
      )
    } catch (error) {
      console.error(error)
      errorToast(
        error instanceof Error
          ? error
          : new Error('Failed to duplicate template'),
      )
    } finally {
      setDuplicateLoading(false)
    }
  }

  const handleSaveAssignments = async (clientIds: string[]) => {
    if (!manageDialogTemplate) return false
    const template = manageDialogTemplate
    try {
      if (template.is_system) {
        // setAssignments refuses system plans, so diff and link per client.
        await applySystemTemplateAssignments(template, clientIds)
      } else {
        await setAssignments.mutateAsync({
          planId: template.id,
          clientIds,
        })
      }
    } catch (error) {
      console.error(error)
      errorToast(error instanceof Error ? error : null)
      return false
    }
    toast.success('Template assignments updated')
    return true
  }

  const applySystemTemplateAssignments = async (
    template: TemplateRow,
    clientIds: string[],
  ) => {
    const currentIds = new Set(
      template.linked_clients.map((client) => client.id),
    )
    const nextIds = new Set(clientIds)
    const toRemove = [...currentIds].filter((id) => !nextIds.has(id))
    const toAdd = [...nextIds].filter((id) => !currentIds.has(id))
    if (!toRemove.length && !toAdd.length) {
      return
    }

    for (const clientId of toRemove) {
      await unlinkFromClient.mutateAsync({ clientId, planId: template.id })
    }
    for (const clientId of toAdd) {
      await assignToClient.mutateAsync({
        clientId,
        planId: template.id,
        isPrimary: false,
        isVisible: true,
      })
    }

    const byId = new Map((clients ?? []).map((client) => [client.id, client]))
    patchLinkedClients(
      template.id,
      clientIds
        .map((clientId) => byId.get(clientId))
        .filter((client): client is NonNullable<typeof client> =>
          Boolean(client),
        ),
    )
  }

  const handleDeleteTemplate = async (template: TemplateRow) => {
    try {
      await deletePlan.mutateAsync({ planId: template.id })
    } catch {
      return
    }
    toast.success('Template deleted')
  }

  const handleConfirmDelete = async () => {
    if (!deleteTemplate) return
    const template = deleteTemplate
    setDeleteTemplate(null)
    await handleDeleteTemplate(template)
  }

  const columns = useMemo<ColumnDef<TemplateRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Template',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="font-medium">{row.original.name}</span>
            {row.original.is_system && (
              <Badge variant="secondary">Trainrr</Badge>
            )}
          </div>
        ),
      },
      {
        id: 'meals',
        header: 'Meals',
        cell: ({ row }) => row.original.meal_count,
      },
      {
        id: 'linked',
        header: 'Linked Clients',
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={(event) => {
              event.stopPropagation()
              setManageDialogTemplateId(row.original.id)
              setManageDialogOpen(true)
            }}
          >
            <LinkedClientAvatarStack clients={row.original.linked_clients} />
          </Button>
        ),
      },
      {
        id: 'actions',
        cell: ({ row }) => (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="ghost">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    setManageDialogTemplateId(row.original.id)
                    setManageDialogOpen(true)
                  }}
                >
                  <Link2 className="mr-2 h-4 w-4" /> Link clients
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    navigate({
                      to: '/nutrition/$templateId',
                      params: { templateId: row.original.id },
                    })
                  }
                  disabled={row.original.is_system}
                >
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleOpenDuplicateTemplate(row.original)}
                >
                  <Copy className="mr-2 h-4 w-4" /> Duplicate
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setDeleteTemplate(row.original)}
                  disabled={row.original.is_system}
                  variant="destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    [handleOpenDuplicateTemplate, navigate],
  )

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="flex h-full min-h-0 w-full flex-col md:flex">
        <div className="flex min-h-0 flex-1 flex-col gap-4 p-8 pt-6">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 className="text-3xl font-bold tracking-tight">
                Meal Plan Templates
              </h2>
              <p className="text-sm text-muted-foreground">
                Create reusable meal plans and assign them to clients.
              </p>
            </div>
            <Button className="gap-2" onClick={() => setNewDialogOpen(true)}>
              <Plus className="h-4 w-4" /> New Template
            </Button>
          </div>
          <VirtualizedDataTable
            className="min-h-0 flex-1"
            columns={columns}
            data={templates}
            isLoading={isTemplatesPending}
            onRowSelected={(row) =>
              navigate({
                to: '/nutrition/$templateId',
                params: { templateId: row.id },
              })
            }
            showSearchBar
            stickyHeader
            tableContainerClassName="min-h-0 flex-1"
            tableWrapperClassName="h-full min-h-0"
            virtualizedHeight="100%"
            estimateRowHeight={72}
            emptyView={
              <Empty className="border border-dashed">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Utensils className="h-5 w-5" />
                  </EmptyMedia>
                  <EmptyTitle>No meal plan templates yet</EmptyTitle>
                  <EmptyDescription>
                    Create a reusable meal plan to assign to clients.
                  </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <Button
                    className="gap-2"
                    onClick={() => setNewDialogOpen(true)}
                  >
                    <Plus className="h-4 w-4" /> New template
                  </Button>
                </EmptyContent>
              </Empty>
            }
          />
        </div>
      </div>

      <Dialog
        open={newDialogOpen}
        onOpenChange={(open) => {
          if (creatingTemplate) return
          setNewDialogOpen(open)
        }}
        analyticsName="nutrition_new_template_dialog"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New meal plan template</DialogTitle>
          </DialogHeader>
          <Input
            value={newTemplateName}
            onChange={(event) => setNewTemplateName(event.target.value)}
            placeholder="Template name"
            disabled={creatingTemplate}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNewDialogOpen(false)}
              disabled={creatingTemplate}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateTemplate} disabled={creatingTemplate}>
              Create &amp; Edit
              {creatingTemplate && <LoadingSpinner className="ml-2" />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(duplicateTemplate)}
        onOpenChange={(open) => {
          if (!open) {
            setDuplicateTemplate(null)
            setDuplicateLoading(false)
          }
        }}
        analyticsName="nutrition_duplicate_template_dialog"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate meal plan template</DialogTitle>
          </DialogHeader>
          <Input
            value={duplicateTemplateName}
            onChange={(event) => setDuplicateTemplateName(event.target.value)}
            placeholder="Template name"
            disabled={duplicateLoading}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDuplicateTemplate(null)}
              disabled={duplicateLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDuplicateTemplate}
              disabled={duplicateLoading}
            >
              Duplicate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ClientSelectDialog
        open={manageDialogOpen}
        onOpenChange={(open) => {
          setManageDialogOpen(open)
          if (!open) setManageDialogTemplateId(null)
        }}
        clients={clients ?? []}
        initialSelectedIds={
          manageDialogTemplate
            ? manageDialogTemplate.linked_clients.map((client) => client.id)
            : []
        }
        onSave={handleSaveAssignments}
        title={manageDialogTemplate?.name ?? 'Assign meal plan template'}
        description="Link this plan to clients so that changes are synced to all."
        headerIcon={<Link2 className="h-4 w-4" />}
        saveLabel="Save"
        allowEmpty
      />
      <DeleteAlert
        title={`Delete ${deleteTemplate?.name ?? 'template'}?`}
        description={`Are you sure you want to delete ${
          deleteTemplate?.name ?? 'this template'
        }?`}
        open={Boolean(deleteTemplate)}
        onCancel={() => setDeleteTemplate(null)}
        onDelete={handleConfirmDelete}
      />
    </div>
  )
}
